import { type CodeRuleAuditResult, runCodeRuleAudit, screenCodeRuleAudit } from "@/api/codeRuleAudits";
import { useEffect, useRef, useState } from "react";

export const BACKGROUND_AUDIT_DELAY_MS = 1000;

type CodedItem = { code: string };

export type BackgroundCodeRuleAuditInput = {
  enabled: boolean;
  encounterId: string;
  diagnoses: CodedItem[];
  procedures: CodedItem[];
};

export type BackgroundAuditState = {
  phase: "idle" | "checking" | "ready" | "failed";
  result: CodeRuleAuditResult | null;
  resultKey: string | null;
  resultAt: number | null;
  screened: boolean;
};

const IDLE: BackgroundAuditState = { phase: "idle", result: null, resultKey: null, resultAt: null, screened: false };

const FAILED: BackgroundAuditState = { ...IDLE, phase: "failed" };

const NO_CONCERNS: CodeRuleAuditResult = { summary: "", concerns: [] };

const completedAtMs = (completedAt: string | undefined) => {
  const parsed = completedAt === undefined ? Number.NaN : Date.parse(completedAt);
  return Number.isNaN(parsed) ? null : parsed;
};

const uniqueCodes = (items: CodedItem[]) => [...new Set(items.map((item) => item.code))];

const codeListKey = (diagnosisCodes: string[], procedureCodes: string[]) =>
  JSON.stringify([diagnosisCodes, procedureCodes]);

const auditKey = (encounterId: string, codesKey: string) => JSON.stringify([encounterId, codesKey]);

export function useBackgroundCodeRuleAudit({
  enabled,
  encounterId,
  diagnoses,
  procedures,
}: BackgroundCodeRuleAuditInput): BackgroundAuditState {
  const [state, setState] = useState<BackgroundAuditState>(IDLE);
  const diagnosisCodes = uniqueCodes(diagnoses);
  const procedureCodes = uniqueCodes(procedures);
  const codesKey = codeListKey(diagnosisCodes, procedureCodes);
  const hasCodes = diagnosisCodes.length + procedureCodes.length > 0;

  const latestCodesRef = useRef({ diagnoses: diagnosisCodes, procedures: procedureCodes });
  latestCodesRef.current = { diagnoses: diagnosisCodes, procedures: procedureCodes };
  const lastFiredRef = useRef<string | null>(null);

  const stateEncounterRef = useRef(encounterId);

  useEffect(() => {
    if (stateEncounterRef.current !== encounterId) {
      stateEncounterRef.current = encounterId;
      setState(IDLE);
    }
    if (!enabled || encounterId === "" || !hasCodes) {
      lastFiredRef.current = null;
      setState(IDLE);
      return;
    }

    const firedKey = auditKey(encounterId, codesKey);
    if (firedKey === lastFiredRef.current) return;

    const controller = new AbortController();
    let settled = false;
    const timer = setTimeout(() => {
      lastFiredRef.current = firedKey;
      setState((previous) => ({ ...previous, phase: "checking" }));
      const body = { encounterId, ...latestCodesRef.current };
      screenCodeRuleAudit(body, controller.signal)
        .then((likelihood) => {
          if (likelihood === "low") return { result: NO_CONCERNS, resultAt: Date.now(), screened: true };
          if (controller.signal.aborted) throw new DOMException("Aborted", "AbortError");
          return runCodeRuleAudit(body, controller.signal).then(({ completedAt, ...result }) => ({
            result,
            resultAt: completedAtMs(completedAt),
            screened: false,
          }));
        })
        .then(
          ({ result, resultAt, screened }) => {
            settled = true;
            if (!controller.signal.aborted)
              setState({ phase: "ready", result, resultKey: firedKey, resultAt, screened });
          },
          () => {
            settled = true;
            if (!controller.signal.aborted) setState(FAILED);
          },
        );
    }, BACKGROUND_AUDIT_DELAY_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
      if (!settled && lastFiredRef.current === firedKey) lastFiredRef.current = null;
    };
  }, [enabled, encounterId, codesKey, hasCodes]);

  return state;
}
